/**
 * Alpine component: profileForm
 *
 * Gestisce il form profilo "I miei dati" con 4 blocchi indipendenti.
 * Anagrafica/contatto/newsletter fanno un PATCH /v1/users/me con i soli
 * campi del blocco; il blocco "sicurezza" usa PUT /v1/users/me/password.
 * Include: Google Places autocomplete per città, password strength meter.
 */
export function registerProfileForm(Alpine) {
    Alpine.data('profileForm', (userData) => ({
        form: {
            title: userData.title || '',
            name: userData.name || '',
            surname: userData.surname || '',
            fiscal_code: userData.fiscal_code || '',
            sex: userData.sex || '',
            date_of_birth: userData.date_of_birth || '',
            city_of_birth: userData.city_of_birth || '',
            email: userData.email || '',
            mobile_phone: userData.mobile_phone || '',
            phone: userData.phone || '',
            address: userData.address || '',
            house_number: userData.house_number || '',
            zip_code: userData.zip_code || '',
            city: userData.city || '',
            district: userData.district || '',
            password: '',
            password_confirm: '',
            subscribe_newsletter: userData.subscribe_newsletter || false,
        },

        _original: {},
        errors: {},
        saving: { anagrafica: false, contatto: false, sicurezza: false, newsletter: false },
        saved: { anagrafica: false, contatto: false, sicurezza: false, newsletter: false },

        // Password
        showPassword: false,
        showPasswordConfirm: false,
        passwordStrength: 0,

        // Google Places autocomplete
        citySuggestions: [],
        cityLoading: false,
        cityOpen: false,
        _cityDebounce: null,
        _gLocation: null, // raw address_components per il PATCH

        init() {
            this._original = JSON.parse(JSON.stringify(this.form));
        },

        /**
         * Mappa blocco → campi da inviare nel PATCH.
         */
        _blockFields: {
            anagrafica: ['title', 'name', 'surname', 'sex', 'date_of_birth', 'city_of_birth'],
            contatto: ['mobile_phone', 'phone', 'address', 'house_number', 'zip_code', 'city', 'district'],
            sicurezza: ['password'],
            newsletter: ['subscribe_newsletter'],
        },

        // ── Password strength ──────────────────────────────────────
        onPasswordInput() {
            const p = this.form.password;
            let score = 0;
            if (p.length >= 6)  score++;
            if (p.length >= 12) score++;
            if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
            if (/[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p)) score++;
            this.passwordStrength = score;
        },

        get strengthLabel() {
            return ['', 'Debole', 'Discreta', 'Buona', 'Ottima'][this.passwordStrength] || '';
        },

        // ── Google Places autocomplete ─────────────────────────────
        fetchCitySuggestions(val) {
            clearTimeout(this._cityDebounce);
            if (val.length < 2) { this.citySuggestions = []; this.cityOpen = false; return; }
            this._cityDebounce = setTimeout(async () => {
                this.cityLoading = true;
                try {
                    const res = await fetch(window.IKOS.apiBase + '/v1/place/autocomplete?input=' + encodeURIComponent(val));
                    const data = await res.json();
                    this.citySuggestions = data.payload || [];
                    this.cityOpen = this.citySuggestions.length > 0;
                } catch (e) {}
                this.cityLoading = false;
            }, 300);
        },

        async selectCity(place) {
            this.cityOpen = false;
            this.cityLoading = true;
            try {
                const res = await fetch(window.IKOS.apiBase + '/v1/place/details?place_id=' + place.place_id);
                const data = await res.json();
                const components = data.payload?.address_components || [];
                let city = '', zip = '', district = '';
                components.forEach(c => {
                    if (c.types.includes('locality'))                                city     = c.short_name;
                    if (c.types.includes('administrative_area_level_3') && !city)    city     = c.short_name;
                    if (c.types.includes('postal_code'))                             zip      = c.short_name;
                    if (c.types.includes('administrative_area_level_2'))             district = c.short_name;
                });
                this.form.city = city;
                this.form.zip_code = zip;
                this.form.district = district;
                this._gLocation = components;
            } catch (e) {}
            this.cityLoading = false;
        },

        // ── Reset ──────────────────────────────────────────────────
        resetBlock(block) {
            const fields = this._blockFields[block];
            for (const key of fields) {
                this.form[key] = this._original[key] ?? '';
            }
            for (const key of fields) {
                delete this.errors[key];
            }
            this.saved[block] = false;
            if (block === 'sicurezza') {
                this.form.password_confirm = '';
                this.passwordStrength = 0;
                this.showPassword = false;
                this.showPasswordConfirm = false;
                delete this.errors.password_confirm;
            }
        },

        // ── Save ───────────────────────────────────────────────────
        async saveBlock(block) {
            this.errors = {};
            this.saved[block] = false;

            // Il cambio password non passa dal PATCH generico /v1/users/me:
            // quell'endpoint scarta il campo `password` (manca `password_mem`).
            // Usa l'endpoint dedicato PUT /v1/users/me/password.
            if (block === 'sicurezza') {
                return this._savePassword();
            }

            this.saving[block] = true;

            try {
                const fields = this._blockFields[block];
                const body = {};
                for (const key of fields) {
                    const val = this.form[key];
                    if (block === 'sicurezza' && !val) continue;
                    body[key] = val;
                }

                if (body.district) {
                    body.district = body.district.toUpperCase();
                }

                // Includi g_location se disponibile (Google Places components)
                if (block === 'contatto' && this._gLocation) {
                    body.g_location = this._gLocation;
                }

                await this._apiPatch('/v1/users/me', body);

                this.saved[block] = true;
                setTimeout(() => { this.saved[block] = false; }, 3000);
            } catch (err) {
                if (err.validation_messages) {
                    this.errors = {};
                    for (const [field, msgs] of Object.entries(err.validation_messages)) {
                        this.errors[field] = Object.values(msgs)[0] || 'Errore';
                    }
                } else {
                    this.errors._global = err.message || 'Errore di rete';
                }
            } finally {
                this.saving[block] = false;
            }
        },

        // ── Cambio password (blocco "sicurezza") ───────────────────
        /**
         * Cambia la password via PUT /v1/users/me/password.
         * Valida lato client password + conferma prima di chiamare l'API:
         * il backend non valida lunghezza né corrispondenza.
         */
        async _savePassword() {
            delete this.errors.password;
            delete this.errors.password_confirm;

            const pwd     = this.form.password;
            const confirm = this.form.password_confirm;

            if (pwd.length < 8) {
                this.errors.password = 'La password deve avere almeno 8 caratteri.';
                return;
            }
            if (confirm !== pwd) {
                this.errors.password_confirm = 'Le password non coincidono.';
                return;
            }

            // ── Validazione superata: salva via API ──
            this.saving.sicurezza = true;
            try {
                await this._apiSend('PUT', '/v1/users/me/password', { password: pwd });

                this.form.password = '';
                this.form.password_confirm = '';
                this.passwordStrength = 0;
                this.showPassword = false;
                this.showPasswordConfirm = false;

                this.saved.sicurezza = true;
                setTimeout(() => { this.saved.sicurezza = false; }, 3000);
            } catch (err) {
                this.errors.password = err.message || 'Errore durante il cambio password.';
            } finally {
                this.saving.sicurezza = false;
            }
        },

        async _apiPatch(path, body) {
            return this._apiSend('PATCH', path, body);
        },

        async _apiSend(method, path, body) {
            const apiBase = window.IKOS?.apiBase || '';
            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };

            const authToken = window.IKOS?.authToken;
            if (authToken && authToken !== true) {
                headers['Auth-Token'] = authToken;
            }

            const guestId = window.IKOS?.guestId;
            if (guestId) {
                headers['X-Guest-Id'] = guestId;
            }

            const res = await fetch(apiBase + path, {
                method,
                headers,
                body: JSON.stringify(body),
            });

            const data = await res.json();

            if (!res.ok) {
                throw data;
            }

            return data;
        },
    }));
}
